import { differenceInHours, differenceInMinutes, isSameDay as isSameDayFn} from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';

// 计算开始时间与结束时间的时间差
export const durationBetween = (start: Date, end: Date) => {
    const hours = differenceInHours(end, start);
    const minutes = differenceInMinutes(end, start) % 60;
    return `${hours}hr ${minutes}min`;
}

// 计算当前时间与开始时间的时间差
export const durationBetweenNow = (start: Date | string) => {
    const now = new Date();
    return durationBetween(new Date(start), now);
}

// 计算当前时间与开始时间的时间差
export const timeLeftNowMinutes = (start: Date | string) => {
    const now = new Date();
    return differenceInMinutes(new Date(start), now);
}

/**
 * 将ISO 8601格式的日期字符串转换为YYYY/MM/DD格式
 */
export const formatDate = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}/${month}/${day}`;
    } catch (error) {
        return dateString;
    }
};

/**
 * 将时间戳转换为Date对象
 * @param timestamp 时间戳
 * @returns Date对象
 */
export const timestampToDate = (timestamp: number | string): Date => {
    if (typeof timestamp === 'string') {
        timestamp = parseInt(timestamp);
    }
    // 如果是10位数字，认为是秒时间戳
    if (timestamp.toString().length === 10) {
      return new Date(timestamp * 1000);
    }
    // 如果是13位数字，认为是毫秒时间戳
    return new Date(timestamp);
};

/**
 * 将时间戳转换为dayjs对象
 * @param timestamp 时间戳
 * @returns dayjs对象
 */
export const timestampToDayjs = (timestamp: number | string): Dayjs => {
    return dayjs(timestampToDate(timestamp));
};

/**
 * 判断两个日期是否是同一天
 * @param date1 日期1
 * @param date2 日期2
 * @returns 是否是同一天
 */
export const isSameDay = (date1: Date, date2: Date) => {
    return isSameDayFn(date1, date2);
};

/**
 * 将时间戳或ISO日期字符串格式化为YYYY-MM-DD HH:mm格式
 * @param timestamp 时间戳或ISO日期字符串
 * @returns 格式化后的时间字符串
 */
export const formatTimestampToDateTime = (timestamp: number | string): string => {
    if (typeof timestamp === 'string' && isNaN(Number(timestamp))) {
        // 如果是ISO日期字符串，直接用dayjs解析
        return dayjs(timestamp).format('YYYY-MM-DD HH:mm');
    }
    // 如果是时间戳，使用原来的逻辑
    return dayjs(timestampToDate(Number(timestamp))).format('YYYY-MM-DD HH:mm');
}

export const formatTimestampToSecondsTime = (timestamp: number | string): string => {
    if (typeof timestamp === 'string' && isNaN(Number(timestamp))) {
        // 如果是ISO日期字符串，直接用dayjs解析
        return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    }
    // 如果是时间戳，使用原来的逻辑
    return dayjs(timestampToDate(Number(timestamp))).format('YYYY-MM-DD HH:mm:ss');
}

/**
 * 将Date对象转换为当天的开始和结束时间戳
 * @param date Date对象
 * @returns 包含开始和结束时间戳的对象（秒级时间戳）
 */
export const getTimestampsFromDate = (date: Date): { startTimestamp: number; endTimestamp: number } => {
    const dayjsDate = dayjs(date);
    
    return {
        startTimestamp: Math.floor(dayjsDate.startOf('day').valueOf() / 1000),
        endTimestamp: Math.floor(dayjsDate.endOf('day').valueOf() / 1000)
    };
};

/**
 * 根据当前语言格式化预约时间
 * @param timestamp 时间戳或ISO日期字符串
 * @param language 语言代码 (zh, es, en)
 * @returns 格式化后的预约时间字符串
 */
export const formatTimestampByLanguage = (timestamp: number | string, language: string = 'en'): string => {
    if (!timestamp) return '';
    
    let date: Date;
    
    if (typeof timestamp === 'string' && isNaN(Number(timestamp))) {
        // 如果是ISO日期字符串，直接用dayjs解析
        date = dayjs(timestamp).toDate();
    } else {
        // 如果是时间戳，使用原来的逻辑
        date = timestampToDate(Number(timestamp));
    }
    
    // 根据语言设置不同的格式
    switch (language) {
        case 'zh':
        case 'zh-CN':
        case 'zh-TW':
            // 中文格式：YYYY年MM月DD日 HH:mm
            return dayjs(date).format('YYYY/MM/DD HH:mm');
        case 'es':
        case 'es-ES':
        case 'es-MX':
            // 西语格式：DD/MM/YYYY HH:mm
            return dayjs(date).format('DD/MM/YYYY HH:mm');
        case 'en':
        case 'en-US':
        case 'en-GB':
        default:
            // 英文格式：MM/DD/YYYY HH:mm
            return dayjs(date).format('MM/DD/YYYY HH:mm');
    }
};

/**
 * 解析日期字符串为Date对象，使用本地时区避免时区问题
 * @param dateString 日期字符串，格式为 YYYY-MM-DD
 * @returns Date对象，如果解析失败则返回null
 */
export const parseLocalDate = (dateString: string): Date | null => {
    if (!dateString) {
        return null;
    }

    try {
        // 使用dayjs解析日期字符串，dayjs默认使用本地时区
        // 这样可以避免 new Date("YYYY-MM-DD") 被解析为UTC时间的问题
        const parsed = dayjs(dateString, 'YYYY-MM-DD', true); // strict mode
        
        if (!parsed.isValid()) {
            console.warn('Invalid date string:', dateString);
            return null;
        }

        // 验证解析后的日期是否与输入一致（避免时区偏移）
        const year = parsed.year();
        const month = parsed.month() + 1; // dayjs的month从0开始
        const day = parsed.date();
        const [inputYear, inputMonth, inputDay] = dateString.split('-').map(Number);

        if (year === inputYear && month === inputMonth && day === inputDay) {
            return parsed.toDate();
        } else {
            console.warn('Date parsing mismatch:', { input: dateString, parsed: `${year}-${month}-${day}` });
            return null;
        }
    } catch (error) {
        console.warn('Failed to parse date string:', dateString, error);
        return null;
    }
};
/**
 * 取指定 IANA 时区（如 America/Los_Angeles）的当前日期，格式 YYYY/MM/DD。
 * @param timezoneLocation IANA 时区名；为空或非法（后端数据缺失/脏数据）时回落到设备本地时区
 */
export const formatTodayInTimezone = (timezoneLocation?: string | null): string => {
    if (timezoneLocation) {
        try {
            // en-CA 输出 YYYY-MM-DD，是各 locale 里唯一稳定给出该顺序的，再换成斜杠
            return new Intl.DateTimeFormat('en-CA', {
                timeZone: timezoneLocation,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }).format(new Date()).replace(/-/g, '/');
        } catch (error) {
            // 时区名非法时 Intl 抛 RangeError；不能因为一个脏数据让单据渲染不出来
            console.warn('Invalid timezoneLocation, fall back to device timezone:', timezoneLocation, error);
        }
    }
    return dayjs().format('YYYY/MM/DD');
};
